<template>
  <div class="mt-4">
    <div v-if="data.length === 0">
      <graph-skeleton class="mx-3"></graph-skeleton>
    </div>
    <div v-else :style="{ 'height': height }" class="d-flex flex-column">
      <h5 class="mb-4 mt-2 title">{{ title }}</h5>
        <div class="d-flex flex-fill flex-column mb-4">
          <div class="d-flex funnel-row flex-fill" v-for="row, index in data" :key="row.name">
            <span class="d-inline-block h5 align-self-center percent m-0" :style="percentStyles[index]">{{ Math.round(getRowPercent(row)) }}%</span>
            <span class="d-inline-block details align-self-center">
              <div class="details-name">{{ row.name }}</div>
              <div class="text-muted">{{ row.number | addCommas }}</div>
            </span>
            <div class="bar-holder position-relative">
              <div class="triangleHolder" :id="'triangleHolder' + index + _uid" :style="triangleHolderStyles[index][0]">
                <div v-if="triangleStyles && triangleStyles[index]" class="triangle" :style="triangleStyles[index][0]"></div>
              </div><div class="bar" :style="rowStyles[index]"></div><div class="triangleHolder" :style="triangleHolderStyles[index][1]">
                <div v-if="triangleStyles && triangleStyles[index]" class="triangle" :style="triangleStyles[index][1]"></div>
              </div>
              <div class="retainedValueIcon rounded px-1 small text-secondary" v-if="index < data.length - 1">
                {{ Math.round(getRetainedValue(index) * 100) }}%
              </div>
            </div>
          </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.title {
  font-weight: 500;
  font-size: 20px !important;
}
.retainedValueIcon {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 1000;
  background-color: var(--light);
}
.triangle {
  height: 100%;
}
.bar {
  height: 100%;
  display: inline-block;
}
.triangleHolder {
  height: 100%;
  display: inline-block;
  overflow: hidden;
}
.percent {
  width: 70px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.details {
  width: 100px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-size: 70%;
}
.details-name {
  font-weight: 600;
}
.bar-holder {
  flex: 1;
}
.funnel-row {
  margin-top: 1px;
  margin-bottom: 1px;
}
</style>

<script>
import numeral from 'numeral'
import { getCSSVariable } from '@/components/utils.js'
import tinycolor from 'tinycolor2'
import GraphSkeleton from '@/reports/components/graphs/GraphSkeleton'

export default {
  props: {
    /**
     * data is an ordered array of objects, where each object is like
     * { name, number }
     */
    data: { type: Array, required: true },
    title: { type: String, default: '' },
    height: { type: String, default: '300px' }
  },
  data () {
    return {
      maxColorLighten: 72,
      rowHeight: 50,
      triangleStyles: null
    }
  },
  created() {
    this.$nextTick(function() {
      window.addEventListener('resize', this.newTriangleStyles);

      //Init
      this.newTriangleStyles()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.newTriangleStyles);
  },
  methods: {
    getRowPercent (row) {
      if (typeof row === 'number') {
        row = this.data[row]
      }
      return (row.number / this.maxNumber) * 100
    },
    getNextRowPercent (index) {
      if (index === this.data.length - 1) {
        let row = this.data[index]
        return this.getRowPercent(row)
      } else {
        let row = this.data[index + 1]
        return this.getRowPercent(row)
      }
    },
    getRetainedValue (index) {
      let num = this.getNextRowPercent(index) / this.getRowPercent(index)
      if (isNaN(num) || num === Infinity) {
        return 0
      } else {
        return num
      }
    },
    getColor(percent) {
      let lightenPercent = (100 - percent) * ((100 - this.maxColorLighten) / 100)
      let color = tinycolor(this.primaryColor)
      let newColor = color.lighten(lightenPercent)
      return newColor
    },
    newTriangleStyles () {
      this.$nextTick(function() {
        this.triangleStyles = this.getTriangleStyles()
      })
    },
    getTriangleStyles () {
      let styles = []
      for (let index of this.data.keys()) {
        let percent = this.getRowPercent(index)
        let triangleHolder = document.getElementById('triangleHolder' + index + this._uid)
        if (!triangleHolder) {
          styles.push('')
        } else {
          let width = triangleHolder.offsetWidth
          let height = triangleHolder.offsetHeight
          let rotationDegs = Math.atan(height / width) * (180 / Math.PI)
          styles.push(['width: 1000%; background-color: ' + this.getColor(percent) + '; transform:translate(-90%) rotate(' + rotationDegs + 'deg);transform-origin: bottom right;', 'width: 1000%; background-color: ' + this.getColor(percent) + '; transform:rotate(-' + rotationDegs + 'deg);transform-origin: bottom left;'
          ])
        }
      }
      return styles
    },
  },
  computed: {
    percentStyles () {
      let styles = []
      for (let index of this.data.keys()) {
        let percent = this.getRowPercent(index)
        styles.push('color: ' + this.getColor(percent) + ';')
      }
      return styles
    },
    triangleHolderStyles () {
      let styles = []
      for (let index of this.data.keys()) {
        let percent = this.getRowPercent(index)
        let nextRowPercent = this.getNextRowPercent(index)
        let triangleWidth = (percent - nextRowPercent) / 2
        let marginWidth = (100 - percent) / 2
        styles.push(['margin-left: ' + marginWidth + '%;width:' + triangleWidth + '%;','margin-right: ' + marginWidth + '%;width:' + triangleWidth + '%;'])
      }
      return styles
    },
    rowStyles () {
      let styles = []
      for (let index of this.data.keys()) {
        let percent = this.getRowPercent(index)
        let nextRowPercent = this.getNextRowPercent(index)
        styles.push('width:' + nextRowPercent + '%; background-color: ' + this.getColor(percent) + ';')
      }
      return styles
    },
    primaryColor () {
      return getCSSVariable('primary')
    },
    maxNumber () {
      let max = 0
      for (let row of this.data) {
        if (row.number > max) {
          max = row.number
        }
      }
      return max
    }
  },
  watch: {
    data () {
      this.newTriangleStyles()
    }
  },
  filters: {
    addCommas: function (number) {
      return numeral(number).format('0,0')
    }
  },
  components: {
    GraphSkeleton
  }
}
</script>


// WEBPACK FOOTER //
// src/reports/components/graphs/FunnelGraph.vue